import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Users, Bell, UserCircle, LayoutDashboard, MessagesSquare, Trophy } from "lucide-react";
import { toast } from "sonner";
import { getMySummary, isAdminRole } from "@/lib/api";
import { registerSW, syncExistingSubscription } from "@/lib/push";
import { isNativeApp, initNativePush } from "@/lib/nativePush";
import AiAssistantWidget from "@/components/AiAssistantWidget";

const REP_TABS = [
  { to: "/m", icon: Home, label: "Ana Sayfa", end: true },
  { to: "/m/leads", icon: Users, label: "Musteriler" },
  { to: "/m/notifications", icon: Bell, label: "Bildirimler" },
  { to: "/m/profile", icon: UserCircle, label: "Profil" },
];
const ADMIN_TABS = [
  { to: "/m", icon: LayoutDashboard, label: "Panel", end: true },
  { to: "/m/leads", icon: Users, label: "Musteriler" },
  { to: "/m/conversations", icon: MessagesSquare, label: "Konusmalar" },
  { to: "/m/team", icon: Trophy, label: "Ekip" },
  { to: "/m/profile", icon: UserCircle, label: "Profil" },
];

function readCachedUser() {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
}

export default function MobileLayout() {
  const [user, setUser] = useState(readCachedUser);
  const [summary, setSummary] = useState(null);
  const admin = isAdminRole(user);
  const tabs = admin ? ADMIN_TABS : REP_TABS;
  const lastUnread = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const refreshSummary = useCallback(async () => {
    try {
      const res = await getMySummary();
      setSummary(res.data);
      const unread = res.data?.unread_notifications ?? 0;
      if (lastUnread.current !== null && unread > lastUnread.current) {
        toast("Yeni bildirim var", { description: "Bildirimler sekmesinden goruntuleyin" });
      }
      lastUnread.current = unread;
    } catch { /* offline or session issue - ignore */ }
  }, []);

  useEffect(() => {
    const cached = readCachedUser();
    if (cached) setUser(cached);
    if (isNativeApp()) {
      initNativePush({
        navigate,
        onForeground: (n) => { toast(n.title || "UcarFlow", { description: n.body || "" }); refreshSummary(); },
      });
    } else {
      registerSW().then(() => syncExistingSubscription().catch(() => {}));
    }
  }, [navigate, refreshSummary]);

  useEffect(() => {
    refreshSummary();
    const id = setInterval(refreshSummary, 30000);
    const onFocus = () => refreshSummary();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [refreshSummary, location.pathname]);

  const ctx = useMemo(() => ({ user, summary, refreshSummary }), [user, summary, refreshSummary]);
  const unread = summary?.unread_notifications || 0;
  const hideTabs = /^\/m\/conversations\/[^/]+$/.test(location.pathname);

  return (
    <div className="min-h-screen bg-[#F4F4F5] font-manrope text-slate-900 max-w-[720px] mx-auto relative" data-testid="mobile-layout">
      <main className={hideTabs ? "" : "pb-[calc(76px+env(safe-area-inset-bottom))]"}>
        <Outlet context={ctx} />
      </main>
      {!hideTabs && <AiAssistantWidget mobile />}

      {!hideTabs && <nav
        className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[720px] bg-white/95 backdrop-blur-md border-t border-slate-200 z-40"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-tabbar"
      >
        <div className={`grid h-[64px] ${tabs.length === 5 ? "grid-cols-5" : "grid-cols-4"}`}>
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`mtab-${t.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-1 text-[11px] font-semibold tracking-wide transition-colors duration-200 ${
                  isActive ? "text-red-600" : "text-slate-400 active:text-slate-600"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`absolute top-0 h-[3px] w-10 rounded-b-full bg-red-600 transition-opacity duration-200 ${isActive ? "opacity-100" : "opacity-0"}`} />
                  <span className="relative">
                    <t.icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2.4 : 1.9} />
                    {t.to === "/m/notifications" && unread > 0 && (
                      <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="mtab-unread-badge">
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </span>
                  <span>{t.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>}
    </div>
  );
}
